












































































































import { Component, Vue, Watch } from 'vue-property-decorator';

import { GQL_employeeSearchVariables, GQL_employeeSearch, GQL_employeeSearch_employeeSearch } from '@/queries/__generated__/employeeSearch';

import { SmartQuery } from 'vue-apollo-decorator';

import { IRules, sValidation } from '@/components/UI/Form/ValidationModule';

import PhoneInput from '@/components/PhoneInput.vue';
import { GQL_EmployeeCreate } from '@/queries/global';
import { validateRequired, validateType, validateMod11, validateLength } from '@/components/UI/Form/Validations';

import { EmployeeSearchMatchedData, norwegianSsnToBirthDate, employeeCreateObject } from '@/app/Employee';
import merge from 'lodash/merge';
import { GQL_unitPaycheckSetting } from '@/queries/__generated__/unitPaycheckSetting';
import { UNIT_PAYCHECK_SETTINGS } from '@/queries/CompanySetting';
import { Mod11 } from '@/utils/checksum';
import { ALL_COUNTRIES } from '@/queries/Country';
import { GQL_allCountries_allCountries } from '@/queries/__generated__/allCountries';
import gql from 'graphql-tag';

const EMPLOYEE_SEARCH = gql`
  query employeeSearch(
    $ssn: String,
    $tssn: String,
    $email: String!,
    $phone: String!,
  ) {
    employeeSearch(
      ssn: $ssn,
      tssn: $tssn,
      email: $email,
      phone: $phone,
    ) {
      emailMatch
      phoneMatch
      ssnMatch
      tssnMatch
      unit {
        id
        address
        zip
        city
        phone
        employeeUser {
          id
          firstName
          lastName
        }
        country {
          code
        }
      }
    }
  }
`;

import dayjs from 'dayjs';

type WithCountryCodes<T> = T & {
  countryCode: string;
  cellphoneCountryCode: string;
}

type EmployeeSearchMatchType = 'NO_MATCH' | 'FULL_MATCH' | 'PARTIAL_MATCH';

const socialNumberValidation: sValidation = ({ formElements }) =>
  (formElements?.ssn?.dataValue || '') ||
  (formElements?.tssn?.dataValue || '');

export const socialNumberModValidation: sValidation = values => {
  if (!values.dataValue || values.dataValue.length !== 11) return true;

  return (
    validateMod11()(values) &&
    validateMod11({
      format: v => String(v).substring(0, 10),
      weights: Mod11.ssn10thDigitWeights,
    })(values)
  );
};

@Component({
  components: {
    PhoneInput,
  },
})
export default class EmployeeSearch extends Vue {
  @SmartQuery(ALL_COUNTRIES) private allCountries!: GQL_allCountries_allCountries[];

  @SmartQuery<EmployeeSearch, GQL_unitPaycheckSetting>({
    query: UNIT_PAYCHECK_SETTINGS,
    result({ data }) {
      this.employee.unitId = data.currentUnit.id;
      this.employee.countryCode = data.currentUnit.country.code;
      this.employeeSearch.countryCode = data.currentUnit.country.code;
      this.employeeSearch.cellphoneCountryCode = data.currentUnit.country.code;
      this.employee.creditDays = data.currentUnit.unitSetting.unitPaycheckSetting.defaultCreditDays;
      this.employee.vacationMoneyPercentage = data.currentUnit.unitSetting.unitPaycheckSetting.defaultVacationMoneyPercentage;
    },
  }) private currentUnit!: GQL_unitPaycheckSetting;

  private employee: GQL_EmployeeCreate = employeeCreateObject();

  private validFormSync = true;
  private isLoading = false;

  private rules: IRules = {
    ssn: [
      {
        validation: socialNumberValidation,
        message: i18n => i18n.t('Error.required', { field: this.t_attr('Employee', 'socialSecurityNumber') }),
        triggerBy: [ 'tssn' ],
      },
      { validation: validateLength(11), message: i18n => i18n.t('Error.length', { length: 11 }) },
      { validation: socialNumberModValidation, message: i18n => i18n.t('Error.invalid', { field: this.t_attr('Employee', 'socialSecurityNumber') }) },
      {
        validation: ({dataValue}) => {
          if (!dataValue) return true;
          return dayjs.strict(norwegianSsnToBirthDate(dataValue, "")).isValid();
        },
        message: i18n => i18n.t('Error.dateFromSsnNotValid'),
      },
    ],
    tssn: [
      {
        validation: socialNumberValidation,
        message: i18n => i18n.t('Error.required', { field: this.t_attr('Employee', 'temporarySocialSecurityNumber') }),
        triggerBy: [ 'ssn' ],
      },
      { validation: validateLength(11), message: i18n => i18n.t('Error.length', { length: 11 }) },
      { validation: socialNumberModValidation, message: i18n => i18n.t('Error.invalid', { field: this.t_attr('Employee', 'temporarySocialSecurityNumber') }) },
      {
        validation: ({dataValue}) => {
          if (!dataValue) return true;
          return dayjs.strict(norwegianSsnToBirthDate("", dataValue)).isValid();
          },
        message: i18n => i18n.t('Error.dateFromTssnNotValid'),
      },
    ],
    phone: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: i18n.t('Employee.cellphone') }) } ],
    email: [
      { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: i18n.t('Employee.email') }) },
      { validation: validateType('email'), message: i18n => i18n.t('Error.email', { field: i18n.t('Employee.email') }) },
    ],
    countryCode: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: i18n.t('Address.country') }) } ],
  };

  private EMPLOYEE_SEARCH = EMPLOYEE_SEARCH;
  private employeeSearch: WithCountryCodes<GQL_employeeSearchVariables> = {
    ssn: null,
    tssn: null,
    email: '',
    phone: '',
    countryCode: '',
    cellphoneCountryCode: '',
  };

  private employeeSearchResults: GQL_employeeSearch_employeeSearch | null = null;

  private isSearched = false;

  private get matchType(): EmployeeSearchMatchType {
    if (!this.employeeSearchResults) {
      return 'NO_MATCH';
    } else if (
      this.employeeSearchResults.emailMatch &&
      this.employeeSearchResults.phoneMatch &&
      this.employeeSearchResults.ssnMatch &&
      (!this.employeeSearch.tssn || this.employeeSearchResults.tssnMatch)
    ) {
      return 'FULL_MATCH';
    } else if (
      this.employeeSearchResults.emailMatch ||
      this.employeeSearchResults.phoneMatch ||
      this.employeeSearchResults.ssnMatch ||
      (!this.employeeSearch.tssn || this.employeeSearchResults.tssnMatch)
    ) {
      return 'PARTIAL_MATCH';
    } else {
      return 'NO_MATCH';
    }
  }

  private get employeeFields(): EmployeeSearchMatchedData {
    return {
      phoneCountryCode: this.employeeSearch.cellphoneCountryCode,
      firstName: this.employeeSearchResults?.unit?.employeeUser?.firstName || '',
      lastName: this.employeeSearchResults?.unit?.employeeUser?.lastName || '',
      user: {
        login: this.employeeSearch.email,
        cellphone: this.employeeSearch.phone,
        cellphoneCountryCode: this.employeeSearch.cellphoneCountryCode,
      },
      countryCode: this.employeeSearch.countryCode,
      socialSecurityNumber: this.employeeSearch.ssn || '',
      temporarySocialSecurityNumber: this.employeeSearch.tssn || '',
      address: this.employeeSearchResults?.unit?.address || '',
      zip: this.employeeSearchResults?.unit?.zip || '',
      city: this.employeeSearchResults?.unit?.city || '',
      phone: this.employeeSearchResults?.unit?.phone || '',
      birthDate: norwegianSsnToBirthDate(this.employeeSearch.ssn || '', this.employeeSearch.tssn || ''),
    };
  }

  private searchAgain(): void {
    this.isSearched = false;
    this.employeeSearchResults = null;
  }

  private openSupport(): void {
    window.open(process.env.VUE_APP_FAKTURABANK_HTTP);
  }

  private resetEmployeeForm(): void {
    this.employee = employeeCreateObject();
  }

  private feedbackClass(value: boolean): string {
    return value ? 'positive' : 'negative';
  }

  private searchForEmployee(): void {
    this.isLoading = true;

    this.$apollo.query({
      query: EMPLOYEE_SEARCH,
      variables: {
        ...this.employeeSearch,
        ssn: this.employeeSearch.ssn ? this.employeeSearch.ssn : null,
        tssn: this.employeeSearch.tssn ? this.employeeSearch.tssn : null,
      },
      fetchPolicy: 'no-cache',
    }).then(({ data }: { data: GQL_employeeSearch }) => {
      this.employeeSearchResults = data.employeeSearch;
      merge(this.employee, this.employeeFields);
      this.isSearched = true;
    }).finally(() => {
      this.isLoading = false;
    });
  }

  // Required so the input wouldn't be invalid when emptied.
  // Also contributes to sending cleaner request.
  @Watch('employeeSearch.ssn')
  private setSSNtoNullIfEmpty(): void {
    if (this.employeeSearch.ssn === '') this.employeeSearch.ssn = null;
  }

  private convertEmailToLowerCase(): void {
    this.employeeSearch.email = this.employeeSearch.email.toLowerCase();
  }

  // Required so the input wouldn't be invalid when emptied.
  // Also contributes to sending cleaner request.
  @Watch('employeeSearch.tssn')
  private setTSSNtoNullIfEmpty(): void {
    if (this.employeeSearch.tssn === '') this.employeeSearch.tssn = null;
  }
}
